import {
  isBrandedSite,
  useAuthProvider,
  useDeviceTypes,
} from "@hopper-b2b/utilities";
import { SessionInfo } from "@hopper-b2b/hopper-auth";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import config from "../../utils/config";
import "./styles.scss";
import { ClientName, SliceStopCountFilter } from "@hopper-b2b/types";
import {
  AdaUserMetaFields,
  BottomSheetContentEnum,
  ChatFab,
  setBottomSheetContent,
  useModalAlertObserver,
  useSupportChatbot,
  useSupportChatbotPath,
} from "@hopper-b2b/chat";
import {
  PATH_FLIGHTS_BOOK,
  PATH_HOMES,
  PATH_HOTELS_BOOK,
  PATH_HOTELS_BOOK_AVAILABILITY,
  PATH_HOTELS_NEW_CONFIRMATION,
  PATH_HOTELS_SHOP_DETAILS,
} from "@hopper-b2b/hopper-utils";
import { DeepLink, DeepLinkEnum } from "@b2bportal/chat-api";
import { useNavigate } from "react-router-dom-v5-compat";
import { useDispatch } from "react-redux";

export const CHAT_SUPPORT_QUERY_PARAM = "chat-support";
export const TRIP_ID_QUERY_PARAM = "tripId";

export const SupportModal = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryString = useLocation().search;
  const { matchesMobile } = useDeviceTypes();
  const { state } = useAuthProvider();
  const chatbot = useSupportChatbot();
  const sessionInfo = state?.sessionInfo;

  const metaFields = useMemo(
    () => getUserMetaFieldsFromSessionInfo(sessionInfo),
    [sessionInfo]
  );

  const hidePaths = useMemo(
    () => [
      PATH_FLIGHTS_BOOK,
      PATH_HOMES,
      PATH_HOTELS_BOOK_AVAILABILITY,
      PATH_HOTELS_BOOK,
      PATH_HOTELS_SHOP_DETAILS,
      PATH_HOTELS_NEW_CONFIRMATION,
    ],
    []
  );

  const isOnHidePath = useSupportChatbotPath(hidePaths);
  const isModalVisible = useModalAlertObserver();

  const getTripDetailsPath = (tripId: string) => `/trips/?tripId=${tripId}`;

  const handleDeepLink = (deepLink: DeepLink) => {
    switch (deepLink.DeepLink) {
      case DeepLinkEnum.DeepLinkContactSupport:
        dispatch(setBottomSheetContent(BottomSheetContentEnum.ContactSupport));
        break;
      case DeepLinkEnum.DeepLinkTripDetails: {
        const tripsDetailsPath = getTripDetailsPath(
          deepLink.productInfo.itineraryUuid
        );
        navigate(tripsDetailsPath);
        if (matchesMobile) {
          chatbot.minimizeSupportChatbot();
        }
        break;
      }
      case DeepLinkEnum.DeepLinkTripCancellation: {
        const tripsDetailsPath = getTripDetailsPath(
          deepLink.productInfo.itineraryUuid
        );
        navigate(tripsDetailsPath);
        if (matchesMobile) {
          chatbot.minimizeSupportChatbot();
        }
        break;
      }
      case DeepLinkEnum.DeepLinkHotelSearch: {
        const searchParams = new URLSearchParams();
        searchParams.append("placeId", deepLink.placeId);
        searchParams.append("fromDate", deepLink.checkIn);
        searchParams.append("untilDate", deepLink.checkOut);
        searchParams.append("adultsCount", "2");

        const searchTerm = deepLink.searchTerm;

        const hotelSearchPath = `/stays/availability/${searchTerm}?${searchParams.toString()}`;
        navigate(hotelSearchPath);
        if (matchesMobile) {
          chatbot.minimizeSupportChatbot();
        }
        break;
      }
      case DeepLinkEnum.DeepLinkFlightSearch: {
        const searchParams = new URLSearchParams();
        searchParams.append(
          "tripCategory",
          deepLink.returnDate !== undefined ? "round_trip" : "one_way"
        );
        searchParams.append("adultsCount", "1");
        searchParams.append(
          "stopsOption",
          String(SliceStopCountFilter.ANY_NUMBER)
        );
        searchParams.append("origin", deepLink.origin);
        searchParams.append("destination", deepLink.destination);
        searchParams.append("departureDate", deepLink.departureDate);

        if (deepLink.returnDate) {
          searchParams.append("returnDate", deepLink.returnDate);
        }

        const flightSearchPath = `/flights/shop/?${searchParams.toString()}`;
        navigate(flightSearchPath);
        if (matchesMobile) {
          chatbot.minimizeSupportChatbot();
        }
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (chatbot === undefined || !chatbot.isInitialized) return;

    const shouldShowButton = !isOnHidePath && !isModalVisible;
    chatbot.toggleSupportChatbotButton(shouldShowButton);
  }, [chatbot, isOnHidePath, isModalVisible]);

  useEffect(() => {
    if (
      hasValidUserMetaFields(metaFields) &&
      !isBrandedSite() &&
      chatbot !== undefined &&
      !chatbot.isInitialized
    ) {
      const chatConfig = {
        meta: metaFields,
      };
      chatbot.initialize(chatConfig);
    }
  }, [chatbot, metaFields]);

  useEffect(() => {
    const queryParams = new URLSearchParams(queryString);
    const isSupportEnabled = queryParams.has(CHAT_SUPPORT_QUERY_PARAM);

    if (isSupportEnabled && chatbot !== undefined && chatbot.isInitialized) {
      chatbot.openSupportChatbot();
    }
  }, [chatbot, queryString]);

  return <ChatFab handleDeepLink={handleDeepLink} />;
};

const hasValidUserMetaFields = (metaFields: Partial<AdaUserMetaFields>) =>
  (metaFields.phone_number || metaFields.email) &&
  (metaFields.first_name || metaFields.last_name);

const getUserMetaFieldsFromSessionInfo = (sessionInfo?: SessionInfo) => {
  const {
    hopperUserIdToken = "",
    userInfo,
    userInfoResponse,
  } = sessionInfo ?? {};
  const {
    email: uiEmail,
    firstName: uiFirstName,
    lastName: uiLastName,
    phone: uiPhone,
  } = userInfo ?? {};
  const {
    email: uirEmail = "",
    firstName: uirFirstName = "",
    lastName: uirLastName = "",
    mobileNumber: uirPhone = "",
  } = userInfoResponse ?? {};

  return {
    email: uiEmail || uirEmail,
    env: config.ENV,
    first_name: uiFirstName || uirFirstName,
    last_name: uiLastName || uirLastName,
    phone_number: uiPhone || uirPhone,
    tenant: ClientName.HOPPER,
    user_token: hopperUserIdToken,
  };
};
